/**
 * Variant Change Handler with App Widget Preservation
 * Handles section updates while preserving third-party app widgets
 */

/**
 * Builds a Restock Alerts API request with dynamic values
 * @param {string} email - User's email address
 * @param {string} storeDomain - Store domain (e.g., "store.myshopify.com")
 * @param {string} productId - Numeric product ID
 * @param {string} variantId - Numeric variant ID
 * @returns {Object} Request configuration with body and headers
 */
function buildRestockAlertRequest(email, storeDomain, productId, variantId) {
  return {
    body: JSON.stringify({
      email: email,
      store: storeDomain,
      product: productId,
      variant: variantId,
      url: window.location.href,
      ajax: 'true',
      firstName: '',
      lastName: '',
      daAcceptsMarketing: '1',
    }),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  };
}

/**
 * Extracts dynamic product and variant information from the current page
 * @returns {Object} Object with storeDomain, productId, and variantId
 */
function extractProductInfo() {
  // Get store domain dynamically
  const storeDomain = window.location.hostname;

  // Get product ID (numeric ID, not handle)
  // Note: [name="id"] contains variant ID, not product ID, so we don't use it
  const productIdElement =
    document.querySelector('[data-product-id]') || document.querySelector('input[name="product-id"]');
  const productId = productIdElement
    ? productIdElement.dataset.productId || productIdElement.getAttribute('data-product-id') || productIdElement.value
    : null;

  // Get current variant ID dynamically
  const variantId =
    document.querySelector('input[name="id"]:checked')?.value ||
    document.querySelector('input[name="id"]')?.value ||
    document.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
    new URLSearchParams(window.location.search).get('variant');

  return { storeDomain, productId, variantId };
}

/**
 * Shows an inline error message in the restock widget
 * @param {HTMLElement} container - The widget container element
 * @param {string} message - The error message to display
 */
function showRestockErrorMessage(container, message) {
  // Remove any existing error messages
  const existingError = container.querySelector('.restock-error');
  if (existingError) {
    existingError.remove();
  }

  const errorDiv = document.createElement('div');
  errorDiv.className = 'alert alert-danger restock-error';
  errorDiv.textContent = message;
  errorDiv.style.cssText =
    'background: #f8d7da; color: #721c24; padding: 12px; border: 1px solid #f5c6cb; border-radius: 4px; margin: 10px 0;';

  container.insertBefore(errorDiv, container.firstChild);

  // Auto-remove error after 5 seconds
  setTimeout(function () {
    if (errorDiv.parentNode) {
      errorDiv.remove();
    }
  }, 5000);
}

/**
 * Shows success message and disables the restock alert form
 * @param {HTMLElement} container - The widget container element
 * @param {HTMLElement} emailInput - The email input element
 * @param {HTMLElement} button - The submit button element
 */
function showRestockSuccessMessage(container, emailInput, button) {
  const successDiv = document.createElement('div');
  successDiv.className = 'alert alert-success restock-success';

  // Build DOM structure safely without innerHTML
  const strong = document.createElement('strong');
  strong.textContent = 'Thank you!';
  successDiv.appendChild(strong);
  successDiv.appendChild(document.createTextNode(" We'll notify you when this item is back in stock."));

  successDiv.style.cssText =
    'background: #d4edda; color: #155724; padding: 12px; border: 1px solid #c3e6cb; border-radius: 4px; margin: 10px 0;';

  container.insertBefore(successDiv, container.firstChild);

  // Hide the form after successful signup - cleaner UX
  const form = emailInput.closest('form') || emailInput.parentElement;
  if (form) {
    form.style.display = 'none';
  } else {
    // Fallback: hide individual elements if no form wrapper
    emailInput.style.display = 'none';
    button.style.display = 'none';

    // Also hide any labels or text related to the signup
    const signupText = container.querySelector('p, div, span');
    if (signupText && signupText.textContent.toLowerCase().includes('sign up')) {
      signupText.style.display = 'none';
    }
  }
}

// Use WeakMap to track AbortController per widget element
const abortControllers = new WeakMap();

window.handleVariantChangeWithWidgets = function (element, detail) {
  console.log('Main Product - onVariantChange', detail);

  // Cancel any in-flight variant change requests for this element
  const prevController = abortControllers.get(element);
  if (prevController) {
    prevController.abort();
    console.log('Aborted previous variant change request for this widget');
  }

  const newController = new AbortController();
  abortControllers.set(element, newController);

  const sectionId = element.dataset.section;
  const currentUrl = window.location.href;
  const separator = currentUrl.includes('?') ? '&' : '?';
  const appendedUrl = currentUrl + separator + 'section_id=' + sectionId;

  const sectionElement = element.closest('.shopify-section');
  if (!sectionElement) return;

  // Preserve app widgets before replacement with their parent info
  const widgets = [];

  // Batch widget queries for performance
  const widgetElements = sectionElement.querySelectorAll('.gftFreeGiftWrapper, lb-upsell-widget');
  widgetElements.forEach(function (widgetEl) {
    let type = null;

    if (widgetEl.classList.contains('gftFreeGiftWrapper')) {
      type = 'kite';
    } else if (widgetEl.tagName.toLowerCase() === 'lb-upsell-widget') {
      type = 'selleasy';
    }

    if (type) {
      const parentBlock = widgetEl.closest('.product-info__block');
      if (parentBlock) {
        widgets.push({
          element: widgetEl,
          parentId: parentBlock.getAttribute('data-shopify-editor-block') || null,
          parentClass: parentBlock.className,
          type: type,
        });
      }
    }
  });

  // Don't preserve Restock Alerts widget - let it reload fresh to maintain event handlers
  // const restockWidget = sectionElement.querySelector('.da-restock-alerts-container-v1');
  // We'll let the new section HTML include the fresh Restock Alerts widget

  fetch(appendedUrl, { signal: newController.signal })
    .then(function (res) {
      return res.text();
    })
    .then(function (html) {
      // Parse the new HTML and remove duplicate preserved widgets
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, 'text/html');
      const newSectionHtml = newDoc.querySelector('.shopify-section');

      if (newSectionHtml) {
        // Remove only the widgets we're preserving (Kite and Selleasy) - batch query for performance
        const duplicateWidgets = newSectionHtml.querySelectorAll('.gftFreeGiftWrapper, lb-upsell-widget');
        duplicateWidgets.forEach(function (widget) {
          widget.remove();
        });
        // Keep the fresh Restock Alerts widget in the new HTML

        // Replace the section with cleaned HTML
        sectionElement.outerHTML = newSectionHtml.outerHTML;
      } else {
        // Fallback to original HTML replacement
        sectionElement.outerHTML = html;
      }

      // Get the new section element
      const newSection = document.querySelector('[data-section="' + sectionId + '"]');
      if (!newSection) return;

      const newSectionContainer = newSection.closest('.shopify-section');
      if (!newSectionContainer) return;

      // Re-insert preserved app widgets into their correct parent blocks
      if (widgets.length > 0) {
        widgets.forEach(function (widget) {
          let targetParent = null;
          let insertAfterElement = null;

          // Specific placement logic for each widget type
          if (widget.element.classList.contains('gftFreeGiftWrapper')) {
            // Kite widget should go in .product-info__block.product-price after .product-info__price
            targetParent = newSectionContainer.querySelector('.product-info__block.product-price');
            if (targetParent) {
              insertAfterElement = targetParent.querySelector('.product-info__price');
            }
          } else if (widget.element.tagName.toLowerCase() === 'lb-upsell-widget') {
            // Selleasy widget should go in the LAST .product-info__block within .product-info__sticky
            const productInfoSticky = newSectionContainer.querySelector('.product-info__sticky');
            if (productInfoSticky) {
              const allBlocks = productInfoSticky.querySelectorAll('.product-info__block');
              if (allBlocks.length > 0) {
                targetParent = allBlocks[allBlocks.length - 1];
              }
            }
            // If not found in sticky, try the last block in general product-info
            if (!targetParent) {
              const productInfo = newSectionContainer.querySelector('.product-info');
              if (productInfo) {
                const allBlocks = productInfo.querySelectorAll('.product-info__block');
                if (allBlocks.length > 0) {
                  targetParent = allBlocks[allBlocks.length - 1];
                }
              }
            }
          }

          // Fallback: Try to find the exact same parent block by ID
          if (!targetParent && widget.parentId) {
            targetParent = newSectionContainer.querySelector('[data-shopify-editor-block="' + widget.parentId + '"]');
          }

          // Insert the widget into the target parent
          if (targetParent) {
            // Clear any placeholder content if needed
            if (targetParent.children.length === 1 && targetParent.children[0].innerHTML.trim() === '') {
              targetParent.removeChild(targetParent.children[0]);
            }

            if (insertAfterElement && insertAfterElement.nextSibling) {
              // Insert after specific element
              insertAfterElement.parentNode.insertBefore(widget.element, insertAfterElement.nextSibling);
            } else if (insertAfterElement) {
              // Append if no next sibling
              targetParent.appendChild(widget.element);
            } else {
              // Default append
              targetParent.appendChild(widget.element);
            }
          } else {
            // Fallback: append to the end of product-info
            const productInfo = newSectionContainer.querySelector('.product-info');
            if (productInfo) {
              productInfo.appendChild(widget.element);
            }
          }
        });

        // Re-initialize all app widgets using consolidated function
        setTimeout(function () {
          // Use the consolidated widget initialization function
          if (typeof window.reinitializeAppWidgets === 'function') {
            window.reinitializeAppWidgets();
          }

          // Handle fresh Restock Alerts widget with retry mechanism
          const restockWidget = newSectionContainer.querySelector('.da-restock-alerts-container-v1');
          if (restockWidget) {
            console.log('Found Restock Alerts widget, checking initialization...');

            // Check initialization without redundant reinit calls
            function checkRestockWidget(attempt = 1) {
              console.log(`Checking Restock widget status (attempt ${attempt})`);

              // Don't call reinitializeAppWidgets again - it was already called above

              // Check if widget is properly visible/initialized after a short delay
              setTimeout(function () {
                // Check for Restock Alerts specific elements (they use uppercase EMAIL and custom buttons)
                const emailInput = restockWidget.querySelector(
                  'input[name="EMAIL"], input[type="email"], input[name="email"]',
                );
                const submitButton = restockWidget.querySelector(
                  'button, input[type="submit"], .notify-btn, [class*="notify"], [class*="alert"]',
                );
                const hasVisibleContent = restockWidget.offsetHeight > 0 && restockWidget.offsetWidth > 0;

                if (!emailInput || !submitButton || !hasVisibleContent) {
                  console.log(
                    `Restock widget not fully initialized on attempt ${attempt} (email: ${!!emailInput}, button: ${!!submitButton}, visible: ${hasVisibleContent}), retrying...`,
                  );

                  if (attempt < 3) {
                    // Retry with longer delay - trigger reinit and check again
                    setTimeout(function () {
                      if (typeof window.reinitializeAppWidgets === 'function') {
                        window.reinitializeAppWidgets();
                      }
                      setTimeout(function () {
                        checkRestockWidget(attempt + 1);
                      }, 100);
                    }, attempt * 200); // 200ms, 400ms delays
                  } else {
                    console.log(
                      'Restock widget initialization completed after 3 attempts (widget may still be functional)',
                    );
                  }
                } else {
                  console.log(`Restock widget successfully initialized on attempt ${attempt}`);
                }
              }, 100);
            }

            // Start checking process
            setTimeout(function () {
              checkRestockWidget();
            }, 100); // Give initial reinit time to work

            // Execute any script tags within the fresh widget
            const scripts = restockWidget.querySelectorAll('script');
            scripts.forEach(function (script) {
              if (script.textContent && script.textContent.trim()) {
                try {
                  console.log('Executing Restock Alerts script:', script.textContent.substring(0, 100) + '...');
                  // Create a new script element to ensure execution
                  const newScript = document.createElement('script');
                  if (script.src) {
                    newScript.src = script.src;
                  } else {
                    newScript.textContent = script.textContent;
                  }
                  document.head.appendChild(newScript);
                  // Remove it after a brief moment
                  setTimeout(function () {
                    if (newScript.parentNode) {
                      newScript.parentNode.removeChild(newScript);
                    }
                  }, 100);
                } catch (e) {
                  console.error('Script execution failed:', e);
                }
              }
            });

            // Add manual button click handlers since there are no forms
            setTimeout(function () {
              // Find the email input
              const emailInput = restockWidget.querySelector(
                'input[type="email"], input[name*="email"], input[placeholder*="email"], input[type="text"]',
              );

              // Find clickable buttons
              const buttons = restockWidget.querySelectorAll('button, [role="button"], .button, .btn, [onclick]');

              buttons.forEach(function (button, index) {
                // Clone button to remove existing handlers
                const newButton = button.cloneNode(true);
                button.parentNode.replaceChild(newButton, button);

                newButton.addEventListener('click', function (e) {
                  e.preventDefault();
                  e.stopPropagation();

                  if (!emailInput) {
                    showRestockErrorMessage(restockWidget, 'Email input not found');
                    return;
                  }

                  const email = emailInput.value.trim();
                  if (!email) {
                    showRestockErrorMessage(restockWidget, 'Please enter a valid email address');
                    emailInput.focus();
                    return;
                  }

                  if (!email.includes('@')) {
                    showRestockErrorMessage(restockWidget, 'Please enter a valid email address');
                    emailInput.focus();
                    return;
                  }

                  // Extract dynamic values from the page
                  const { storeDomain, productId, variantId } = extractProductInfo();

                  if (!productId && !variantId) {
                    showRestockErrorMessage(
                      restockWidget,
                      'Unable to determine both product and variant information. Please try again.',
                    );
                    return;
                  } else if (!productId) {
                    showRestockErrorMessage(
                      restockWidget,
                      'Unable to determine product information. Please try again.',
                    );
                    return;
                  } else if (!variantId) {
                    showRestockErrorMessage(
                      restockWidget,
                      'Unable to determine variant information. Please try again.',
                    );
                    return;
                  }

                  // Build the API request
                  const { body: requestBody, headers: requestHeaders } = buildRestockAlertRequest(
                    email,
                    storeDomain,
                    productId,
                    variantId,
                  );

                  // Use the working endpoint for Restock Alerts
                  const endpoints = ['https://alertmerestockalerts.herokuapp.com/addemail'];

                  function trySubmit(endpointIndex) {
                    if (endpointIndex >= endpoints.length) {
                      showRestockErrorMessage(
                        restockWidget,
                        'Sorry, there was an error submitting the form. Please try again later.',
                      );
                      return;
                    }

                    const endpoint = endpoints[endpointIndex];

                    fetch(endpoint, {
                      method: 'POST',
                      body: requestBody,
                      headers: requestHeaders,
                    })
                      .then(function (response) {
                        if (response.ok || response.status === 302 || response.status === 200) {
                          showRestockSuccessMessage(restockWidget, emailInput, newButton);
                          return;
                        } else {
                          throw new Error('HTTP ' + response.status);
                        }
                      })
                      .catch(function (error) {
                        trySubmit(endpointIndex + 1);
                      });
                  }

                  trySubmit(0);
                });
              });
            }, 300);
          } else {
            console.log('No Restock Alerts widget found in fresh section');
          }

          window.dispatchEvent(
            new CustomEvent('shopify:section:reloaded', {
              detail: { sectionId: sectionId },
            }),
          );

          // Add fallback classes for browsers without :has() support
          if (typeof CSS !== 'undefined' && CSS.supports && !CSS.supports('selector(:has(*))')) {
            newSectionContainer.querySelectorAll('.product-info__block').forEach(function (block) {
              // Batch widget queries for performance
              const blockWidgets = block.querySelectorAll(
                '.gftFreeGiftWrapper, lb-upsell-widget, .da-restock-alerts-container-v1',
              );
              blockWidgets.forEach(function (widget) {
                if (widget.classList.contains('gftFreeGiftWrapper')) {
                  block.classList.add('has-gftFreeGiftWrapper');
                } else if (widget.tagName.toLowerCase() === 'lb-upsell-widget') {
                  block.classList.add('has-lb-upsell-widget');
                } else if (widget.classList.contains('da-restock-alerts-container-v1')) {
                  block.classList.add('has-da-restock-alerts-container-v1');
                }
              });
            });
          }
        }, 100);
      }
    })
    .catch(function (err) {
      // Don't log abort errors (they're intentional)
      if (err.name === 'AbortError') {
        console.log('Variant change request was cancelled for newer request');
      } else {
        console.error('Variant change error:', err);
      }
    });
};
